import { useCallback, useEffect, useState, forwardRef } from "react";
import styled from "styled-components";
import { Flex, Box, Text } from "rebass/styled-components";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { NextUpEmptyState } from "../NextUpEmptyState";
import { WatchLimitBlocker } from "../WatchLimitBlocker";
import { CountdownCircle } from "../CountdownCircle";
import { NextUpEmbeddablePlaylistViewModel } from "../../ViewModel";
import { LoadMoreCTA } from "../LoadMoreCTA";
import { LoadingSkeleton } from "MVVM/Components/Skeloton";
import { useWatchLimit } from "MVVM/Hooks/useWatchLimit";
import {
  NextUpPlaylistState,
  ActivePlaylistClipState,
  MatchTypeState,
  FallbackPlaylistState,
  PlaylistClipsState,
  ShowWatchLimitBlockerState,
  BlockedPlayerNameState,
  BlockedClipState,
  MidRollCountdownState,
  PlayerInteractedState,
} from "MVVM/State/embeddablePlaylist";
import { formatRelativeTime } from "MVVM/Utilities/Time";
import EmbeddablePlayer from "MVVM/Views/EmbeddablePlayer";
import {
  MidRollAdContainer,
  MidRollCountdownBanner,
  RampCenterOverlay,
} from "MVVM/Views/EmbeddablePlayer/styledComponents";

export const NextUpPlayer = forwardRef<
  HTMLDivElement,
  {
    platform: string;
    playlist: string;
    primaryColor?: string;
    secondaryColor?: string;
    handleMouseEnter?: () => void;
    theme?: string;
  }
>(
  (
    {
      platform,
      playlist,
      primaryColor = "#3273FA",
      secondaryColor = "#1b181f",
      handleMouseEnter,
    },
    ref,
  ) => {
    const setIsNextUpPlaylist = useSetRecoilState(NextUpPlaylistState);
    const activePlaylistClip = useRecoilValue(ActivePlaylistClipState);
    const matchType = useRecoilValue(MatchTypeState);
    const isFallbackPlaylist = useRecoilValue(FallbackPlaylistState);
    const playlistClips = useRecoilValue(PlaylistClipsState);
    const showWatchLimitBlocker = useRecoilValue(ShowWatchLimitBlockerState);
    const blockedPlayerName = useRecoilValue(BlockedPlayerNameState);
    const blockedClip = useRecoilValue(BlockedClipState);

    const midRollCountdown = useRecoilValue(MidRollCountdownState);
    const { handleCloseBlocker } = useWatchLimit();

    const isRampPartner = ["UDOTGG", "U.GG"].includes(platform);
    const isUgg = isRampPartner;

    const [playerInteracted, setPlayerInteracted] = useRecoilState(
      PlayerInteractedState,
    );
    const [isHovered, setIsHovered] = useState(false);

    const handlePlayerHover = useCallback(() => {
      if (!playerInteracted) setPlayerInteracted(true);
      setIsHovered(true);
      handleMouseEnter?.();
    }, [playerInteracted, setPlayerInteracted, handleMouseEnter]);

    const handlePlayerLeave = useCallback(() => {
      setIsHovered(false);
    }, []);

    useEffect(() => {
      return () => {
        setPlayerInteracted(false);
      };
    }, [setPlayerInteracted]);

    const currentClip =
      playlistClips?.find((clip) => clip._id === activePlaylistClip?.id) ||
      playlistClips?.[0];
    const gamePlayed =
      currentClip?.metadata
        ?.find((meta) => meta.key === "Game Played")
        ?.value?.toString() || "";
    const matchDate = formatRelativeTime(
      gamePlayed || currentClip?.createdDate || "",
    );
    const cleanMatchType = matchType?.replace(/ games?$/i, "") || "";

    useEffect(() => {
      setIsNextUpPlaylist(true);
    }, [setIsNextUpPlaylist]);

    const { loadMore, canLoadMore } = NextUpEmbeddablePlaylistViewModel({
      platform,
    });

    const isOPGG = platform === "OPGG" || platform === "OP.GG";

    const showLoadMoreCTA = isUgg && canLoadMore;

    const handleLoadMoreClick = useCallback(() => {
      loadMore();
    }, [loadMore]);

    if (isFallbackPlaylist) return <NextUpEmptyState />;

    return (
      <StyledContainer
        flexDirection="column"
        onMouseEnter={handlePlayerHover}
        onMouseLeave={handlePlayerLeave}
      >
        <PlayerContainer ref={ref}>
          {activePlaylistClip ? (
            <EmbeddablePlayer
              className="next-up-player"
              onPlayEndVariant="nextUp"
              hideRampBottomCenter={isRampPartner}
            />
          ) : (
            <PlayerLoadingSkeleton>
              <LoadingSkeleton width="100%" height="100%" />
            </PlayerLoadingSkeleton>
          )}
          {showWatchLimitBlocker && (
            <WatchLimitBlocker
              onClose={handleCloseBlocker}
              playerName={blockedPlayerName}
              clickedClip={blockedClip}
              playlistId={playlist}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
            />
          )}
        </PlayerContainer>
        <StyledCopyContainer
          className="next-up-copy-container"
          $isOPGG={isOPGG}
          $centerContent={isRampPartner}
        >
          <FooterTextColumn>
            {activePlaylistClip ? (
              <>
                <StyledClipTitle $clamp={isRampPartner}>
                  {activePlaylistClip?.title}
                </StyledClipTitle>
                {!isRampPartner && (
                  <Flex>
                    <StyledClipDetails>{`${activePlaylistClip?.username} - ${cleanMatchType}, ${matchDate}`}</StyledClipDetails>
                  </Flex>
                )}
              </>
            ) : (
              <PlayerLoadingSkeleton>
                <LoadingSkeleton width="35%" />
              </PlayerLoadingSkeleton>
            )}
          </FooterTextColumn>
          {showLoadMoreCTA && (
            <LoadMoreCTA onClick={handleLoadMoreClick} visible={isHovered} />
          )}
        </StyledCopyContainer>
        {isRampPartner && <RampFooterContainer id="bottom-center" />}
        {isRampPartner && midRollCountdown !== null && (
          <MidRollFullOverlay>
            <MidRollAdContainer>
              <RampCenterOverlay id="center-overlay" />
            </MidRollAdContainer>
            <MidRollCountdownBanner style={{ marginBottom: 12 }}>
              <span>Next video playing in...</span>
              <CountdownCircle countdown={midRollCountdown} max={8} size={28} />
            </MidRollCountdownBanner>
          </MidRollFullOverlay>
        )}
      </StyledContainer>
    );
  },
);

const StyledClipTitle = styled(Text)<{ $clamp?: boolean }>`
  color: ${({ theme }) => theme.colors.white};
  font-size: 12px;
  font-weight: 700;
  ${({ $clamp }) =>
    $clamp &&
    `
    max-width: 25%;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  `}
`;

const StyledClipDetails = styled(Text)`
  color: #cddcfe;
  font-size: 10px;
  font-weight: 500;
`;

const StyledContainer = styled(Flex)`
  container-type: inline-size;
  container-name: next-up-container;

  height: 100%;
  position: relative;
  width: 100%;
`;

const PlayerContainer = styled(Box)`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  height: 100%;

  .next-up-player {
    height: 100%;
    width: 100%;
  }

  .next-up-player .embeddable-player {
    height: 100% !important;
  }

  .next-up-player .video-js {
    height: 100% !important;
    width: 100% !important;
  }

  .next-up-player [data-vjs-player] {
    height: 100% !important;
  }

  .next-up-player [data-vjs-player] > div {
    height: 100% !important;
  }

  .next-up-player > .overlay {
    height: calc(100% - 54px) !important;
  }

  .next-up-player .vjs-control-bar {
    z-index: 10 !important;
  }

  .vjs-tech {
    border-radius: 8px;
    height: 100% !important;
    width: 100% !important;
  }
`;

const PlayerLoadingSkeleton = styled(Box)`
  border-radius: 8px;
  height: 100%;
  width: 100%;
`;

const StyledCopyContainer = styled(Flex)<{
  $isOPGG: boolean;
  $centerContent?: boolean;
}>`
  background: ${({ $isOPGG }) => ($isOPGG ? "#31313C" : "#191937")};
  height: 54px;
  flex-direction: row;
  align-items: center;
  flex-shrink: 0;
  gap: 10px;
  padding: 10px;
`;

const FooterTextColumn = styled(Flex)`
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 5px;
`;

const RampFooterContainer = styled(Box)`
  position: absolute;
  bottom: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 50px;
  z-index: 15;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

const MidRollFullOverlay = styled(Flex)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.85);
`;
